import { Avatar, Box, Button, CardProps, Chip, Divider } from "@mui/material";
import { UserCardData } from "../../../../types/user-types";
import { formatDateTime } from "../../../../utils/date-time-utility";
import StatusModal from "../../../StatusModal";
import useDisclosure from "../../../../hooks/use-disclosure";
import RenderWithTooltip from "../../../RenderWithTooltip";
import OutlineCard from "../../../OutlineCard";
import Text from "../../../Text";
import {
  DEFAULT_BG_COLOR,
  DEFAULT_BG_LABEL_COLOR_GREEN,
  DEFAULT_BG_LABEL_COLOR_INACTIVE,
  DEFAULT_FONTSIZE,
  DEFAULT_LABEL_COLOR,
  DEFAULT_LABEL_COLOR_2,
  DEFAULT_LABEL_COLOR_GREEN,
  DEFAULT_LABEL_COLOR_INACTIVE,
  DEFAULT_USER_SRC,
} from "../../../../utils/utils";
import CopyButton from "../../../../utils/util-components/CopyButton";
import VerifiedIcon from "@mui/icons-material/DoneOutlined";
import Flex from "../../../Flex";
import EmailVerificationStatus from "../../../EmailVerificationStatus";

interface CustomCardProps {
  userData: UserCardData;
  handleOpenEditModal: () => void;
  handleOpenProfileModal: () => void;
  onStatusChange: () => Promise<any>;
}

const CustomCard: React.FC<CustomCardProps & CardProps> = ({
  userData,
  handleOpenEditModal,
  handleOpenProfileModal,
  onStatusChange,
  ...others
}) => {
  const statusDisclosure = useDisclosure();

  return (
    <OutlineCard
      sx={{
        width: "332px",
        height: "310px",
        border: "1px solid #E7E7E7",
        borderRadius: "8px",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
      {...others}
    >
      <StatusModal
        w="28rem"
        id={statusDisclosure.data?.uuid}
        content={"You want to change the status ?"}
        onConfirmation={() =>
          onStatusChange().then(() => statusDisclosure.close())
        }
        iconColor="primary.main"
        open={statusDisclosure.opened}
        onClose={statusDisclosure.close}
      />

      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          paddingBottom: "8px",
          borderBottom: "1px solid #E7E7E7",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Text
            size={DEFAULT_FONTSIZE.fontSize}
            color={DEFAULT_LABEL_COLOR}
            fontWeight={400}
            lineHeight={1.2}
          >
            {(userData?.firstName ?? "") + " " + (userData?.lastName ?? "-")}
          </Text>
          <Chip
            label={userData?.role || "-"}
            size="small"
            sx={{
              height: "22px",
              fontSize: "12px",
              backgroundColor: "#e5f3ff",
              color: "primary.main",
              borderRadius: "4px",
              padding: "4px 8px",
              "& .MuiChip-label": {
                padding: "0",
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <Chip
            label={userData.active ? "Active" : "Inactive"}
            size="small"
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "1.2",
              backgroundColor: userData?.active
                ? DEFAULT_BG_LABEL_COLOR_GREEN
                : DEFAULT_BG_LABEL_COLOR_INACTIVE,
              color: userData?.active
                ? DEFAULT_LABEL_COLOR_GREEN
                : DEFAULT_LABEL_COLOR_INACTIVE,
              borderRadius: "16px",
              padding: "2px 10px",
            }}
            onClick={() => statusDisclosure.open(userData)}
          />
        </Box>
      </Box>

      {/* Avatar Section */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Avatar
          alt={userData?.firstName + " " + userData?.lastName}
          src={userData?.avatar ?? DEFAULT_USER_SRC}
          sx={{
            width: "80px",
            height: "80px",
            border: "none",
          }}
        />
      </Box>

      {/* Info Section */}
      <Flex flexDirection={"column"} gap={0}>
        {/* Last Login */}
        <Flex alignItems={"center"} gap={0.5} minHeight={"20px"}>
          <Text color={DEFAULT_LABEL_COLOR_2} sx={{ flexShrink: 0 }}>
            Last Login :
          </Text>
          <Box sx={{ flexGrow: 1, overflow: "hidden", minWidth: 0 }}>
            <RenderWithTooltip
              value={
                userData.lastLogin ? formatDateTime(userData.lastLogin) : "-"
              }
            />
          </Box>
        </Flex>

        {/* Contact Number */}
        <Flex alignItems={"center"} gap={0.5} minHeight={"20px"}>
          <Text color={DEFAULT_LABEL_COLOR_2} sx={{ flexShrink: 0 }}>
            Contact Number :
          </Text>
          <Box sx={{ flexGrow: 1, overflow: "hidden", minWidth: 0 }}>
            <RenderWithTooltip value={userData?.phone ?? "-"} />
          </Box>
          {userData?.phone && userData.phone !== "-" && (
            <CopyButton
              sx={{ padding: "2px" }}
              onClick={() => {
                navigator.clipboard.writeText(userData?.phone);
              }}
            />
          )}
        </Flex>

        {/* Email */}
        <Flex alignItems={"center"} gap={0.5} minHeight={"20px"}>
          <Text color={DEFAULT_LABEL_COLOR_2} sx={{ flexShrink: 0 }}>
            Email :
          </Text>
          <Box
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              <RenderWithTooltip value={userData?.email} />
            </Box>

            <EmailVerificationStatus
              isVerified={userData.emailVerified}
              verifiedIcon={VerifiedIcon}
            />

            {userData?.email && userData.email !== "-" && (
              <CopyButton
                sx={{ padding: "2px" }}
                onClick={() => {
                  navigator.clipboard.writeText(userData?.email ?? "");
                }}
              />
            )}
          </Box>
        </Flex>
      </Flex>

      {/* Actions Section */}
      <Box
        sx={{
          borderTop: "1px solid #E7E7E7",
          paddingTop: "8px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "32px",
        }}
      >
        <Button
          variant="text"
          sx={{
            color: "primary.main",
          }}
          onClick={() => handleOpenProfileModal()}
        >
          View Profile
        </Button>

        <Divider
          orientation="vertical"
          sx={{
            height: "24px",
            borderColor: DEFAULT_BG_COLOR,
            width: "1px",
          }}
        />

        <Button
          variant="text"
          sx={{
            color: "primary.main",
          }}
          onClick={() => handleOpenEditModal()}
        >
          Edit Profile
        </Button>
      </Box>
    </OutlineCard>
  );
};

export default CustomCard;
